import { startCase } from "lodash";
import React from "react";

import { Icon, NoData, RenderWhen, TableDisplayTitle, Time } from "@components";
import { Grid } from "@components/Grid";
import { Tooltip } from "@components/tooltip";
import { ThirdPartyMuteTooltip } from "@modules/network/ThreatIntel/common";
import { Source } from "../../../@types/common";
import ExpandableOverlay from "../../common/ExpandableOverlay";

import styles from "./network-lists.module.scss";

const sourcesSchema = {
  fields: [
    {
      name: "source",
      displayTitle: (
        <TableDisplayTitle className="bold">Source</TableDisplayTitle>
      ),
      sortable: false,
      className: "overflow-ellipsis",
      style: { width: "20%", padding: "12px 20px" },
      component: (item: Source) => {
        const showMuteNote = Boolean(item.provider);
        return (
          <div className="flex">
            <span>{item.display ?? <NoData />}</span>
            <RenderWhen condition={showMuteNote}>
              <ThirdPartyMuteTooltip sourceName={item?.source} />
            </RenderWhen>
          </div>
        );
      }
    },
    {
      name: "update_time",
      displayTitle: (
        <TableDisplayTitle className="bold">
          <>
            Update time
            <Tooltip
              placement="top"
              content="Time when the information from the source was last updated."
            >
              <Icon name="info-circle" />
            </Tooltip>
          </>
        </TableDisplayTitle>
      ),
      sortable: false,
      component: (item: Source) => (
        <RenderWhen
          condition={Boolean(item?.update_time)}
          fallback={<NoData />}
        >
          <Time
            time={item?.update_time}
            className="time-exact"
            withTitle
            isRelative
          />
        </RenderWhen>
      ),
      style: {
        width: "20%",
        padding: "12px 20px"
      },
      className: "overflow-ellipsis"
    },
    {
      name: "detect_time",
      displayTitle: (
        <TableDisplayTitle className="bold">
          <>
            Detection time
            <Tooltip
              placement="top"
              content="Time when the URL was last detected or given a category by the source."
            >
              <Icon name="info-circle" />
            </Tooltip>
          </>
        </TableDisplayTitle>
      ),
      sortable: false,
      component: (item: Source) => (
        <RenderWhen
          condition={Boolean(item?.detect_time)}
          fallback={<NoData />}
        >
          <Time
            time={item?.detect_time}
            className="time-exact"
            withTitle
            isRelative
          />
        </RenderWhen>
      ),
      style: {
        width: "20%",
        padding: "12px 20px"
      },
      className: "overflow-ellipsis"
    },
    {
      name: "status",
      displayTitle: (
        <TableDisplayTitle className="bold">Detection</TableDisplayTitle>
      ),
      sortable: false,
      component: (source: Source) => (
        <RenderWhen condition={Boolean(source.detection)} fallback={<NoData />}>
          <div
            className={`${styles.classification} ${
              styles[source.detection?.toLowerCase()]
            }`}
          >
            {source.detection.capitalize()}
          </div>
        </RenderWhen>
      ),
      style: {
        width: "20%",
        padding: "12px 20px"
      },
      className: "threat-table-cell"
    },
    {
      name: "category",
      displayTitle: (
        <TableDisplayTitle className="bold">Category</TableDisplayTitle>
      ),
      sortable: false,
      component: (item: Source) => (
        <ExpandableOverlay
          placement="top"
          renderKey="tpr-list"
          itemMap={startCase}
          items={item?.categories}
        />
      ),
      style: {
        width: "20%",
        padding: "12px 20px"
      }
    }
  ]
};
const ThirdPartyReputationList: React.FC<{ sources: Source[] }> = ({
  sources
}) => (
  <Grid
    data={sources}
    schema={sourcesSchema}
    testId="nti_third_party_rep_table"
  />
);

export default ThirdPartyReputationList;
